import { useTheme } from './ThemeProvider';

/**
 * Hook to access colors from the theme
 */
export const useThemeColors = () => {
  const { colors } = useTheme();
  return colors;
};

/**
 * Hook to get score color based on score level or value
 */
export const useScoreColor = (score: string | number) => {
  const { colors } = useTheme();
  
  if (typeof score === 'string') {
    switch (score.toLowerCase()) {
      case 'none':
      case 'low risk':
      case 'minimal':
        return colors.score.none;
      case 'low':
        return colors.score.low;
      case 'moderate':
      case 'medium':
        return colors.score.moderate;
      case 'high':
        return colors.score.high;
      case 'critical':
      case 'severe':
        return colors.score.critical;
      default:
        return colors.score.none;
    }
  }
  
  // Handle numeric scores (scale 0-100)
  if (typeof score === 'number') {
    if (score < 20) return colors.score.none;
    if (score < 40) return colors.score.low;
    if (score < 70) return colors.score.moderate;
    if (score < 90) return colors.score.high;
    return colors.score.critical;
  }
  
  return colors.score.none;
};

/**
 * Hook to get text color based on background color
 */
export const useTextColorForBackground = (backgroundColor: string) => {
  const { colors } = useTheme();
  
  // Check if light or dark background
  // Simple implementation - for a more accurate one, calculate luminance
  const colorMap: {[key: string]: string} = {
    [colors.score.none]: colors.text.primary,
    [colors.score.low]: colors.text.light,
    [colors.score.moderate]: colors.text.light,
    [colors.score.high]: colors.text.light,
    [colors.score.critical]: colors.text.light,
    [colors.primary.main]: colors.text.light,
    [colors.secondary.main]: colors.text.light,
    [colors.accent]: colors.text.light,
    [colors.background.default]: colors.text.primary,
    [colors.background.alt]: colors.text.primary,
  };
  
  return colorMap[backgroundColor] || colors.text.primary;
};

/**
 * Hook to get status color based on status string
 */
export const useStatusColor = (status: string) => {
  const { colors } = useTheme();
  
  switch (status.toLowerCase()) {
    case 'active':
    case 'success':
    case 'completed':
    case 'approved':
      return colors.success;
    case 'pending':
    case 'processing':
    case 'in progress':
      return colors.info;
    case 'warning':
    case 'attention':
      return colors.warning;
    case 'error':
    case 'failed':
    case 'rejected':
      return colors.danger;
    default:
      return colors.secondary.main;
  }
};

/**
 * Get alpha version of a color
 * @param color Base color (hex)
 * @param alpha Alpha value (0-1)
 */
export const getAlphaColor = (color: string, alpha: number) => {
  // Convert hex to rgb
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}; 